import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Maximize2, X } from "lucide-react";
import { Loader2 } from "lucide-react";

// Load the worker from the locally-installed pdfjs-dist (matches react-pdf's
// bundled API version) and append pdfjs.version as a cache-bust query so the
// browser always fetches the worker that matches the current API after any
// dependency upgrade.
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
pdfjs.GlobalWorkerOptions.workerSrc = `${pdfWorkerUrl}${
  pdfWorkerUrl.includes("?") ? "&" : "?"
}v=${pdfjs.version}`;

interface PDFViewerProps {
  url: string;
  onComplete?: () => void;
  compact?: boolean;
}

export function PDFViewer({ url, onComplete, compact = false }: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(compact ? 0.5 : 1.0);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenScale, setFullscreenScale] = useState<number>(1.0);

  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const [previewWidth, setPreviewWidth] = useState<number>(0);
  const [fullscreenWidth, setFullscreenWidth] = useState<number>(0);

  useEffect(() => {
    const el = previewContainerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      setPreviewWidth(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!isFullscreen) return;
    const el = fullscreenContainerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      setFullscreenWidth(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isFullscreen]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const goToPreviousPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    const nextPage = pageNumber + 1;
    setPageNumber(Math.min(nextPage, numPages));
    
    // Mark as complete when reaching the last page
    if (nextPage === numPages && onComplete) {
      onComplete();
    }
  };

  const zoomIn = () => {
    if (isFullscreen) {
      setFullscreenScale((prev) => Math.min(prev + 0.2, 2.0));
    } else {
      setScale((prev) => Math.min(prev + 0.2, 2.0));
    }
  };

  const zoomOut = () => {
    if (isFullscreen) {
      setFullscreenScale((prev) => Math.max(prev - 0.2, 0.5));
    } else {
      setScale((prev) => Math.max(prev - 0.2, compact ? 0.4 : 0.5));
    }
  };

  const currentScale = isFullscreen ? fullscreenScale : scale;

  const renderControls = (inFullscreen: boolean = false) => (
    <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-3 ${inFullscreen ? 'bg-background border-b' : 'bg-gradient-to-r from-muted/50 to-muted/30 rounded-lg border border-border/50'} shadow-sm`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPreviousPage}
          disabled={pageNumber <= 1}
          className="transition-all hover:scale-105 hover:shadow-md disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Prev</span>
        </Button>
        
        <Badge variant="secondary" className="px-3 py-1 font-medium">
          Page {pageNumber} of {numPages}
        </Badge>

        <Button
          variant="outline"
          size="sm"
          onClick={goToNextPage}
          disabled={pageNumber >= numPages}
          className="transition-all hover:scale-105 hover:shadow-md disabled:opacity-50"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={zoomOut}
          disabled={currentScale <= (compact ? 0.4 : 0.5)}
          className="transition-all hover:scale-105 disabled:opacity-50"
        >
          <ZoomOut className="h-4 w-4" />
        </Button>

        <Badge variant="outline" className="px-3 py-1 font-mono">
          {Math.round(currentScale * 100)}%
        </Badge>

        <Button
          variant="outline"
          size="sm"
          onClick={zoomIn}
          disabled={currentScale >= 2.0}
          className="transition-all hover:scale-105 disabled:opacity-50"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>

        <div className="h-4 w-px bg-border" />

        {!inFullscreen && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFullscreen(true)}
            className="transition-all hover:scale-105"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}

        <Button
          variant="default"
          size="sm"
          asChild
          className="transition-all hover:scale-105 hover:shadow-md"
        >
          <a href={url} download target="_blank" rel="noopener noreferrer">
            <Download className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Download</span>
          </a>
        </Button>
      </div>
    </div>
  );

  const getPageWidth = (containerWidth: number, viewScale: number) => {
    // Leave room for padding + borders so we don't clip/crop.
    const safeWidth = Math.max(containerWidth - 24, 240);
    return Math.floor(safeWidth * viewScale);
  };

  const renderDocument = (viewScale: number, maxHeight?: string) => (
    <div className={`relative border border-border rounded-lg bg-gradient-to-br from-background to-muted/20 shadow-lg`}>
      <div
        ref={previewContainerRef}
        className="overflow-auto rounded-lg flex items-center justify-center p-2"
        style={maxHeight ? { maxHeight } : undefined}
      >
        {loading && (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <div className="relative">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="absolute inset-0 h-8 w-8 rounded-full border-2 border-primary/20 animate-pulse" />
            </div>
            <p className="text-sm font-medium">Loading PDF...</p>
          </div>
        )}
        
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading=""
          error={
            <div className="text-center p-8 space-y-4">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <span className="text-destructive text-2xl">✕</span>
              </div>
              <p className="text-destructive font-medium">Failed to load PDF</p>
              <p className="text-sm text-muted-foreground">The document could not be displayed</p>
              <Button asChild variant="outline" className="mt-4">
                <a href={url} download target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF instead
                </a>
              </Button>
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            width={previewWidth ? getPageWidth(previewWidth, viewScale) : undefined}
            scale={previewWidth ? undefined : viewScale}
            renderTextLayer={!compact}
            renderAnnotationLayer={!compact}
            className="shadow-xl rounded-lg border border-border/20"
          />
        </Document>
      </div>
    </div>
  );

  return (
    <>
      <div className="space-y-4">
      {renderControls(false)}
      {renderDocument(scale, compact ? "320px" : undefined)}

        {!compact && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <kbd className="px-2 py-1 bg-muted rounded border border-border text-foreground font-mono text-xs">←</kbd>
            <kbd className="px-2 py-1 bg-muted rounded border border-border text-foreground font-mono text-xs">→</kbd>
            <span>to navigate pages</span>
          </div>
        )}
      </div>

      {/* Fullscreen Dialog */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>PDF Viewer</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-3 border-b bg-background">
              <div className="flex items-center gap-2 flex-wrap flex-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPreviousPage}
                  disabled={pageNumber <= 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Prev
                </Button>
                
                <Badge variant="secondary" className="px-3 py-1 font-medium">
                  Page {pageNumber} of {numPages}
                </Badge>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFullscreenScale(prev => Math.max(prev - 0.2, 0.5))}
                  disabled={fullscreenScale <= 0.5}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>

                <Badge variant="outline" className="px-3 py-1 font-mono">
                  {Math.round(fullscreenScale * 100)}%
                </Badge>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFullscreenScale(prev => Math.min(prev + 0.2, 2.0))}
                  disabled={fullscreenScale >= 2.0}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>

                <div className="h-4 w-px bg-border" />

                <Button
                  variant="default"
                  size="sm"
                  asChild
                >
                  <a href={url} download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFullscreen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div
              ref={fullscreenContainerRef}
              className="flex-1 overflow-auto bg-muted/20 flex items-start justify-center p-4"
            >
              <Document
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div className="flex flex-col items-center gap-3 text-muted-foreground p-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm font-medium">Loading PDF...</p>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  width={fullscreenWidth ? getPageWidth(fullscreenWidth, fullscreenScale) : undefined}
                  scale={fullscreenWidth ? undefined : fullscreenScale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="shadow-xl rounded-lg border border-border/20"
                />
              </Document>
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground p-2 border-t bg-background">
              <kbd className="px-2 py-1 bg-muted rounded border border-border text-foreground font-mono text-xs">←</kbd>
              <kbd className="px-2 py-1 bg-muted rounded border border-border text-foreground font-mono text-xs">→</kbd>
              <span>to navigate pages</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
